import SectionTitle from "../components/SectionTitle";
import { Sunrise, Palmtree, Music, Waves, Utensils, Plane, MapPin } from "lucide-react";

const day = (num, date, title, dress, items, color) => ({ num, date, title, dress, items, color });

const days = [
  day("01", "Thursday · May 13", "Arrive & kick it off", "Relaxed beach casual", [
    { time: "All day", label: "Suggested arrival into POP (about 30 min from Villa Taina)" },
    { time: "3:00 PM", label: "Check-in opens at Villa Taina" },
    { time: "6:30 PM", label: "Welcome drinks at the beachfront bar" },
    { time: "8:00 PM", label: "Welcome dinner · Open mic night, theme: \"Rachel Stories\"" },
  ], "terracotta"),
  day("02", "Friday · May 14", "The Time Capsule Party", "Boats and hoes", [
    { time: "Morning", label: "Optional kite-surfing lesson on Kite Beach" },
    { time: "Afternoon", label: "Yacht ride to Cayo Arena" },
    { time: "8:00 PM", label: "The Time Capsule Party (location TBD)" },
    { time: "Late", label: "Dancing until late with our favorite songs throughout the years" },
  ], "sage"),
  day("03", "Saturday · May 15", "The Big One", "Main party night, bring the one nice outfit", [
    { time: "11:00 AM", label: "Recovery brunch, long and slow" },
    { time: "Afternoon", label: "Open. Pool. Beach. Nap. Repeat." },
    { time: "7:30 PM", label: "Dinner at the villa" },
    { time: "9:30 PM", label: "The big party with band, DJ, and dancing until very late" },
  ], "gold"),
  day("04", "Sunday · May 16", "Farewell breakfast", "Sunglasses required", [
    { time: "9:00 AM", label: "Farewell breakfast on the terrace" },
    { time: "11:00 AM", label: "Checkout" },
    { time: "PM", label: "Departures — or extend and stay a few extra days" },
  ], "terracotta"),
];

export default function EventPage() {
  return (
    <div className="bg-cream">
      {/* Hero */}
      <section className="relative pt-20 pb-16 md:pt-28 md:pb-20 overflow-hidden" data-testid="event-hero">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 md:px-10 relative">
          <p className="text-xs tracking-[0.4em] uppercase text-terracotta font-medium mb-4">The Event</p>
          <h1 className="font-display text-6xl md:text-8xl font-black text-ink leading-[0.9] max-w-4xl">
            The trip, <span className="italic text-sage-dark">the plan</span>, the whole deal.
          </h1>
          <p className="text-lg md:text-xl text-muted_ink mt-6 max-w-2xl">
            A long weekend in the Caribbean's most vibrant water-sports town. Here's what we're doing and why you'll love it.
          </p>
        </div>
      </section>

      {/* Why Cabarete */}
      <section className="py-16 md:py-20" data-testid="why-cabarete">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-5 gap-12">
          <div className="md:col-span-2">
            <SectionTitle eyebrow="Why" title="Cabarete." />
          </div>
          <div className="md:col-span-3 space-y-5 text-lg leading-relaxed text-muted_ink">
            <p>
              Cabarete is a small laid-back town on the north coast of the Dominican Republic. It's
              world-famous with kite-surfers, full of beachfront restaurants where your toes are in the sand,
              and warm year-round. Mid-May averages 85°F with low humidity and the occasional afternoon shower.
            </p>
            <p>
              It's easy from most US cities (direct-ish from NYC, Boston, Miami, DC, Nashville), you don't need a visa,
              and everything runs on island time. That's the whole point.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Facts row */}
      <section className="bg-sand py-14 border-y border-shell" data-testid="quick-facts-row">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: Plane, t: "Travel", v: "POP (~30m) · STI (1.5h) · skip SDQ" },
            { icon: Sunrise, t: "Weather", v: "~85°F · low humidity · rain briefly" },
            { icon: Palmtree, t: "Vibe", v: "Beach · music · zero stress" },
            { icon: Music, t: "Main Event", v: "Sat night party · band + DJ" },
          ].map((f, i) => (
            <div key={i} className="text-left">
              <f.icon className="w-6 h-6 text-terracotta mb-3" />
              <p className="font-display italic text-gold text-xs tracking-[0.3em] uppercase mb-1">{f.t}</p>
              <p className="font-display text-ink text-lg font-bold leading-tight">{f.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What to expect */}
      <section className="py-20 md:py-24" data-testid="what-to-expect">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <SectionTitle eyebrow="What to expect" title="Low-key. Warm. Yours to shape.">
            Everyone books their own flights. Rooms at Villa Taina are first come, first serve, and there are only 38.
            No kids at the villa so if you're bringing the family, check out the <span className="sun-underline">other hotels page</span>.
            Activities are optional and you're free to join when and where you want.
          </SectionTitle>
        </div>
      </section>

      {/* Itinerary Timeline */}
      <section className="py-16 md:py-24 bg-sand border-t border-shell" data-testid="itinerary">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <SectionTitle eyebrow="Day by day" title="The itinerary" align="center">
            Times are approximate. I reserve the right to change my mind.
          </SectionTitle>

          <div className="mt-10 space-y-8 md:space-y-10">
            {days.map((d) => (
              <div
                key={d.num}
                className="relative grid md:grid-cols-[120px_1fr] gap-6 bg-cream border border-shell rounded-lg overflow-hidden"
                data-testid={`day-${d.num}`}
              >
                <div className={`p-6 md:p-8 ${d.color === "terracotta" ? "bg-terracotta" : d.color === "sage" ? "bg-sage" : "bg-gold"} text-cream flex md:flex-col md:justify-between items-start`}>
                  <div>
                    <p className="font-display italic text-cream/80 text-xs tracking-widest uppercase mb-1">Day</p>
                    <p className="font-display text-5xl md:text-6xl font-black leading-none">{d.num}</p>
                  </div>
                  <p className="ml-4 md:ml-0 md:mt-6 text-xs tracking-wider uppercase">{d.date}</p>
                </div>

                <div className="p-6 md:p-8">
                  <h3 className="font-display text-3xl md:text-4xl font-black text-ink leading-tight mb-1">{d.title}</h3>
                  <p className="text-sm text-terracotta font-medium tracking-wide mb-5">Dress: {d.dress}</p>

                  <ul className="space-y-3">
                    {d.items.map((it, i) => (
                      <li key={i} className="flex items-start gap-4 text-muted_ink" data-testid={`day-${d.num}-item-${i}`}>
                        <span className="font-mono text-xs bg-sand border border-shell rounded px-2 py-1 mt-0.5 min-w-[5rem] text-center text-ink">{it.time}</span>
                        <span className="text-base leading-relaxed">{it.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-12 text-center text-sm text-muted_ink flex items-center justify-center gap-2">
            <MapPin className="w-4 h-4 text-terracotta" />
            All events within walking distance of Villa Taina unless noted
          </p>
        </div>
      </section>
    </div>
  );
}
